import { v4 as uuidV4 } from 'uuid'

import { getNestedUiSchemaElements } from '../../deprecated/journey'
import type { AtLeast } from '../../types'
import type * as next from '../../types/next'
import {
  CONTROL_NAME,
  getBlockNameByScope,
  logicStringsToObjects,
  parseLogicActionTarget,
  parseLogicConditionSource
} from '../../utils'
import type {
  BlockUiSchema,
  Journey as JourneyV3,
  LogicAction,
  LogicActionType,
  LogicCondition
} from '../../utils'

/**
 * A singleton instance which keeps the migrated
 * data in memory for other migration tasks to use
 *
 * @example
 * ```
 * const instance = MigrationStore.getInstance()
 *
 * ... When upgrading blocks ...
 * instance.addBlock(blockId, block)
 *
 * ... Usage ...
 * instance.getBlock(blockId)
 * ```
 */
export class MigrationStore {
  private static instance: MigrationStore

  /**
   * Migrated blocks
   */
  private blocks: Record<
    next.BlockId,
    Omit<next.Block, 'stepId' | 'orderIndex'>
  > = {}

  /** Migrated logics */
  private logics: Record<next.LogicId, next.Logic> = {}

  constructor() {}

  public static getInstance(): MigrationStore {
    if (!MigrationStore.instance) {
      MigrationStore.instance = new MigrationStore()
    }

    return MigrationStore.instance
  }

  /**
   * Stores the migrated block in memory
   *
   * @param blockId UUID of the block
   * @param block Essential data of the migrated block
   */
  public addBlock(
    blockId: next.BlockId,
    block: Omit<next.Block, 'stepId' | 'orderIndex'>
  ) {
    this.blocks[blockId] = block
  }

  /**
   * Retrieves migrated block from memory
   *
   * @param blockId UUID of migrated block
   * @returns EssentialBlock
   */
  public getBlock(
    blockId: next.BlockId
  ): Omit<next.Block, 'stepId' | 'orderIndex'> {
    return this.blocks[blockId]
  }

  public addLogic(logicId: next.LogicId, logic: next.Logic) {
    this.logics[logicId] = logic
  }

  public getLogic(logicId: next.LogicId) {
    return this.logics[logicId]
  }

  public clear() {
    this.blocks = {}
  }
}

/**
 * Convenience method to generate a new unique UUIDv4 among a list of existing Ids.
 *
 * If no existing Ids are provided, the first generate UUID is returned.
 */
export const newId = <T>(existingIds: T[] = []): T => {
  const id = uuidV4() as T

  while (existingIds.includes(id)) {
    return newId<T>(existingIds)
  }

  return id
}

export const blockIdMapper = (
  items: Pick<next.Block, 'id'>[]
): next.BlockId[] => {
  return items.map((item) => item.id)
}

export const findStepById = (
  journey: AtLeast<JourneyV3, 'steps'>,
  stepId: string
) => {
  const index = journey.steps.findIndex((step) => step.stepId === stepId)

  if (index !== -1) {
    return {
      step: journey.steps[index],
      index
    }
  }

  return undefined
}

export const findBlockIdWithStepId = (
  journey: AtLeast<JourneyV3, 'steps'>,
  stepId: string,
  blockName?: string | null
) => {
  const step = findStepById(journey, stepId)

  return step
    ? findBlockIdWithinStep(journey, step.index, blockName)
    : undefined
}

export const parseBlockPath = (path: string | undefined) => {
  const matches = path?.match(/^([^/]+)\/(.+)$/)

  if (!matches) return undefined

  return {
    stepId: matches[1],
    blockName: matches[2]
  }
}

export const getBlockIdFromLogicSource = (
  logicActionType: LogicActionType,
  currentBlockId: string,
  journey: AtLeast<JourneyV3, 'steps' | 'logics'>,
  blockName: string
): string | undefined => {
  const logics = logicStringsToObjects(journey.logics)

  for (const logic of logics) {
    if (!logic.actions || logic.actions.length !== 1) {
      throw new Error('Logic has no or multiple actions')
    }
    if (!logic.conditions || logic.conditions.length !== 1) {
      throw new Error('Logic has no or multiple conditions')
    }
    const action = logic.actions[0]
    const condition = logic.conditions[0]

    const targetBlock = parseLogicActionTarget(action.target)

    if (!targetBlock) {
      throw new Error(`Could not parse target block name in logic`)
    }
    const [stepId, targetBlockName] = targetBlock.split('/')
    const targetBlockId = findBlockIdWithStepId(
      journey,
      stepId,
      targetBlockName
    )

    if (targetBlockId !== currentBlockId) {
      // This logic is not defined for my block
      continue
    }

    if (action.actionType === logicActionType && condition.conditionSource) {
      const sourceBlock = parseLogicConditionSource(condition.conditionSource)

      if (!sourceBlock) {
        throw new Error(`Could not parse source block name in logic`)
      }
      const [sourceStepId, sourceBlockName] = sourceBlock.split('/')

      if (sourceBlockName != blockName) {
        continue
      }

      return findBlockIdWithStepId(journey, sourceStepId, sourceBlockName)
    }
  }

  return undefined
}

export const findBlockIdByLogicCondition = (
  journey: AtLeast<JourneyV3, 'steps'>,
  condition: LogicCondition
): string | undefined => {
  if (condition.conditionSource) {
    const parsed = parseBlockPath(
      parseLogicConditionSource(condition.conditionSource)
    )

    if (parsed === undefined) return undefined

    const reference = findBlockIdWithStepId(
      journey,
      parsed.stepId,
      parsed.blockName
    )

    return reference
  }

  return undefined
}

export const findStepIdByLogicActionTarget = (
  journey: AtLeast<JourneyV3, 'steps'>,
  action: LogicAction
): string | undefined => {
  if (action.target) {
    const parsed = parseLogicActionTarget(action.target)

    if (parsed === undefined) return undefined

    return findStepById(journey, parsed)?.step.stepId
  }

  return undefined
}

/**
 * Convenience method to find a given block's id by their name and Step index.
 */
export const findBlockIdWithinStep = (
  journey: AtLeast<JourneyV3, 'steps'>,
  stepIndex: number,
  blockName: string | null | undefined
): next.BlockId | undefined => {
  const step = journey.steps[stepIndex]

  if (!step) return undefined

  return getNestedUiSchemaElements(step.uischema).find(
    (element) => getBlockNameByScope(element.scope) === blockName
  )?.id
}

/**
 * Convenience method to find a given context parameter's id by their name.
 */
export const findContextParameterIdByName = (
  journey: AtLeast<JourneyV3, 'contextSchema'>,
  paramKey: string | null | undefined
): next.ContextParameterId | undefined => {
  return journey.contextSchema?.find((item) => item.paramKey === paramKey)?.id
}

export const findJourneyLauncherControl = (
  journey: AtLeast<JourneyV3, 'steps'>
): BlockUiSchema | undefined => {
  const control = (journey.steps[0].uischema as any)
    ?.elements?.[0]?.[0] as BlockUiSchema

  if (control && control.type === CONTROL_NAME.JOURNEY_LAUNCHER_CONTROL) {
    return control
  }

  return undefined
}
